/**
 * LmnTileContent — the body of an LMN (Letter of Medical Necessity) product tile.
 * No price, no ATC, no swatches, no promotions.
 *
 * Rendered by `LmnProductTile`. Content (modal copy + CTA label) is authored in
 * the Builder `site-preferences` entry, resolved once in the layout loader and
 * read here via `useLayoutData().lmnContent` (SFCC-11677).
 *
 * Source references:
 * - sfcc-website/org_hec/cartridge/templates/default/product/productTile.isml (lines 57-66)
 * - hec-composable/hec/components/product-tile-cms/productTileCms.tsx
 *
 * Shows:
 * - "What's an LMN?" link (opens modal)
 * - "Get Started" CTA linking to PDP (label from Builder `lmnCallToAction`)
 * - Exclusion disclaimer
 */
import { Link } from "react-router";

import { useURL } from "~/contexts";
import { useLayoutData } from "~/routes/layout";

import { WhatsLmnButton } from "./whats-lmn-button";

interface LmnTileContentProps {
  productId: string;
  productUrl?: string;
}

export function LmnTileContent({ productId, productUrl }: LmnTileContentProps) {
  const url = useURL();
  const { lmnContent } = useLayoutData();
  const resolvedUrl = productUrl || `/${productId}.html`;

  return (
    <>
      <div className="mt-2">
        <WhatsLmnButton
          title={lmnContent.modalTitle}
          content={lmnContent.modalContent}
          link={lmnContent.modalLink}
          data-testid={`lmn-whats-lmn-${productId}`}
        />
      </div>

      <Link
        to={url(resolvedUrl)}
        className="mt-2 block w-full rounded bg-primary px-4 py-3 text-center font-medium text-primary-foreground"
        prefetch="intent"
        data-testid={`lmn-get-started-${productId}`}
      >
        {lmnContent.callToAction}
      </Link>

      <p className="mt-2 text-xs text-gray-500">
        Excluded from promotions and coupons
      </p>
    </>
  );
}
